// Stable identity + ordering for the chapters/insights tree.
//
// Chapters live at projects/{pid}/chapters/{uid} and insights at
// projects/{pid}/chapters/{uid}/insights/{uid}; the doc id IS the uid. Both
// carry an integer `order` (their index within the parent). These helpers are
// pure (no Firebase import) so they run in vitest and in the store alike:
//
//   normalize*  — read side: tolerate legacy docs (no uid/order/topic, string
//                 findings, `quote` keys, leaked `_open` UI flags)
//   canonical*  — write side: the exact doc we persist, fixed shape, no `_`
//                 keys, undefined-free (Firestore rejects undefined)
//   serialize / diff — turn the in-memory tree into per-doc set/delete ops
//                 against a snapshot of what was loaded, so a save touches
//                 only what changed instead of rebuilding the whole tree.

/* global globalThis */
const ID_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
const ID_LENGTH = 20;

// Legacy docs (no `order`, or with leaked `_` keys) get this snapshot marker so
// they always differ from the serialized tree and are rewritten on first save.
export const LEGACY_MARK = "__legacy__";

// Firestore-style auto id: 20 chars from the same alphabet, so new ids are
// indistinguishable from doc ids assigned by addDoc in the old save path.
export function newId() {
  const bytes = new Uint8Array(ID_LENGTH);
  const cryptoObj = typeof globalThis !== "undefined" && globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i = 0; i < ID_LENGTH; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  let out = "";
  for (let i = 0; i < ID_LENGTH; i++) {
    out += ID_ALPHABET[bytes[i] % ID_ALPHABET.length];
  }
  return out;
}

function str(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}

function isUiKey(key) {
  return typeof key === "string" && key.startsWith("_");
}

// [{text}] rows from whatever shape a finding/recommendation arrived in
// (strings from the AI dialog, {text} from the editor).
export function toTextRows(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((f) => f !== null && f !== undefined)
    .map((f) =>
      typeof f === "object" ? { text: str(f.text) } : { text: str(f) }
    );
}

// [{text, selected, name?}] rows; accepts `quote` (formatInsight raw output)
// and plain strings.
export function toQuoteRows(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((q) => q !== null && q !== undefined)
    .map((q) => {
      if (typeof q !== "object") return { text: str(q), selected: false };
      const row = {
        text: str(q.text !== undefined ? q.text : q.quote),
        selected: q.selected === true,
      };
      const name = str(q.name || q.participant).trim();
      if (name) row.name = name;
      return row;
    });
}

function numberOrNull(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

// [{url, path, name, contentType, width?, height?}] rows for the images
// attached to an insight. `url` is the Storage download url (what the UI and
// a copy/paste use), `path` the object path (what a delete needs). Rows
// without a url are dropped.
export function toImageRows(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((img) => img && typeof img === "object" && str(img.url))
    .map((img) => {
      const row = {
        url: str(img.url),
        path: str(img.path),
        name: str(img.name),
      };
      const type = str(img.contentType).trim();
      if (type) row.contentType = type;
      if (numberOrNull(img.width) !== null) row.width = img.width;
      if (numberOrNull(img.height) !== null) row.height = img.height;
      return row;
    });
}

// Report status of an insight: draft (Klad) -> review (Ter review) -> done
// (Klaar). Stored as the machine value; labels live in INSIGHT_STATUS_LABELS.
export const INSIGHT_STATUSES = ["draft", "review", "done"];
export const INSIGHT_STATUS_LABELS = {
  draft: "Klad",
  review: "Ter review",
  done: "Klaar",
};

// Accepts machine values and the Dutch labels (text mode, older data).
export function normalizeStatus(v) {
  const s = String(v == null ? "" : v)
    .trim()
    .toLowerCase();
  if (s === "review" || s === "ter review" || s === "ter-review")
    return "review";
  if (s === "done" || s === "klaar") return "done";
  return "draft";
}

function extraKeys(raw, known) {
  const out = {};
  Object.keys(raw || {}).forEach((k) => {
    if (known.has(k) || isUiKey(k)) return;
    const v = raw[k];
    if (v === undefined || typeof v === "function") return;
    out[k] = v;
  });
  return out;
}

const INSIGHT_KEYS = new Set([
  "uid",
  "title",
  "topic",
  "order",
  "status",
  "strength",
  "findings",
  "quotes",
  "images",
]);
const CHAPTER_KEYS = new Set([
  "uid",
  "title",
  "order",
  "recommendations",
  "recommendations_title",
  "insights",
]);

// Read side. `fallbackUid` is the Firestore doc id; it wins over a stored uid
// because the old save path could leave a stale one behind.
export function normalizeInsight(raw, fallbackUid) {
  const r = raw || {};
  const uid = str(fallbackUid) || str(r.uid) || newId();
  return {
    ...extraKeys(r, INSIGHT_KEYS),
    uid,
    title: str(r.title),
    topic: str(r.topic),
    order: numberOrNull(r.order),
    status: normalizeStatus(r.status),
    strength: numberOrNull(r.strength),
    findings: toTextRows(r.findings),
    quotes: toQuoteRows(r.quotes),
    images: toImageRows(r.images),
  };
}

export function normalizeChapter(raw, fallbackUid, insights) {
  const r = raw || {};
  const uid = str(fallbackUid) || str(r.uid) || newId();
  return {
    ...extraKeys(r, CHAPTER_KEYS),
    uid,
    title: str(r.title),
    order: numberOrNull(r.order),
    recommendations: toTextRows(r.recommendations),
    recommendations_title: r.recommendations_title
      ? str(r.recommendations_title)
      : null,
    insights: Array.isArray(insights) ? insights : [],
  };
}

// Factories for the editor. Every new item gets its uid up front so
// draggable item-keys and the diff save can rely on it.
export function newInsight(partial = {}) {
  return {
    uid: newId(),
    title: "",
    topic: "",
    status: "draft",
    strength: null,
    findings: [{ text: "" }],
    quotes: [{ text: "", selected: false }],
    images: [],
    ...partial,
  };
}

export function newChapter(partial = {}) {
  return {
    uid: newId(),
    title: "",
    insights: [],
    recommendations: [],
    recommendations_title: null,
    ...partial,
  };
}

// Stable sort: numeric `order` ascending, items without one after, both
// groups keeping their incoming relative order.
export function sortByOrder(list) {
  return (list || [])
    .map((item, i) => ({ item, i }))
    .sort((a, b) => {
      const ao = numberOrNull(a.item && a.item.order);
      const bo = numberOrNull(b.item && b.item.order);
      if (ao !== null && bo !== null) return ao - bo || a.i - b.i;
      if (ao !== null) return -1;
      if (bo !== null) return 1;
      return a.i - b.i;
    })
    .map((x) => x.item);
}

// Mutates in place: gives every chapter/insight a uid and makes them unique
// within the tree (a duplicated block in text mode, or a copy-pasted object,
// must never make two docs collide on one path).
export function ensureIds(chapters) {
  const seen = new Set();
  (chapters || []).forEach((c) => {
    if (!c || typeof c !== "object") return;
    if (typeof c.uid !== "string" || !c.uid || seen.has(c.uid)) c.uid = newId();
    seen.add(c.uid);
    if (!Array.isArray(c.insights)) c.insights = [];
    c.insights.forEach((ins) => {
      if (!ins || typeof ins !== "object") return;
      if (typeof ins.uid !== "string" || !ins.uid || seen.has(ins.uid)) {
        ins.uid = newId();
      }
      seen.add(ins.uid);
    });
  });
  return chapters;
}

// Write side. `order` defaults to whatever the item carries (null for legacy
// docs) so a snapshot of raw Firestore data can be canonicalised too.
export function canonicalInsightDoc(insight, order) {
  const n = normalizeInsight(insight, insight && insight.uid);
  const docOrder = order === undefined ? n.order : order;
  return {
    ...extraKeys(insight, INSIGHT_KEYS),
    uid: n.uid,
    title: n.title,
    topic: n.topic,
    order: docOrder,
    status: n.status,
    strength: n.strength,
    findings: n.findings,
    quotes: n.quotes,
    images: n.images,
  };
}

export function canonicalChapterDoc(chapter, order) {
  const n = normalizeChapter(chapter, chapter && chapter.uid, []);
  const docOrder = order === undefined ? n.order : order;
  return {
    ...extraKeys(chapter, CHAPTER_KEYS),
    uid: n.uid,
    title: n.title,
    order: docOrder,
    recommendations: n.recommendations,
    recommendations_title: n.recommendations_title,
  };
}

// Deterministic JSON (sorted keys, recursively) so two docs with the same
// content compare equal regardless of key insertion order.
export function stableStringify(value) {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return "[" + value.map((v) => stableStringify(v)).join(",") + "]";
  }
  const keys = Object.keys(value)
    .filter((k) => value[k] !== undefined)
    .sort();
  return (
    "{" +
    keys
      .map((k) => JSON.stringify(k) + ":" + stableStringify(value[k]))
      .join(",") +
    "}"
  );
}

export function chapterPath(chapterUid) {
  return `chapters/${chapterUid}`;
}

export function insightPath(chapterUid, insightUid) {
  return `chapters/${chapterUid}/insights/${insightUid}`;
}

// The in-memory tree as the docs we would persist: order = index at both
// levels. Requires uids (call ensureIds first). Returns { [path]: {data, str} }.
export function serializeChapters(chapters) {
  const docs = {};
  (chapters || []).forEach((c, ci) => {
    const cdoc = canonicalChapterDoc(c, ci);
    docs[chapterPath(cdoc.uid)] = { data: cdoc, str: stableStringify(cdoc) };
    (c.insights || []).forEach((ins, ii) => {
      const idoc = canonicalInsightDoc(ins, ii);
      docs[insightPath(cdoc.uid, idoc.uid)] = {
        data: idoc,
        str: stableStringify(idoc),
      };
    });
  });
  return docs;
}

// Snapshot entry for a doc as it sits in Firestore. Legacy docs are marked so
// the first save rewrites them in canonical form (adds order, drops `_open`).
export function snapshotEntryFromRaw(raw, kind) {
  const r = raw || {};
  const hasUiKeys = Object.keys(r).some(isUiKey);
  if (hasUiKeys || numberOrNull(r.order) === null) return LEGACY_MARK;
  const canonical =
    kind === "chapter" ? canonicalChapterDoc(r) : canonicalInsightDoc(r);
  return stableStringify(canonical);
}

// { [path]: str } from serialized docs — what the store keeps after a save.
export function snapshotFromDocs(docs) {
  const out = {};
  Object.keys(docs || {}).forEach((path) => {
    out[path] = docs[path].str;
  });
  return out;
}

// set for every new/changed doc, delete for every doc no longer in the tree.
export function diffChapterTrees(prevSnapshot, nextDocs) {
  const prev = prevSnapshot || {};
  const next = nextDocs || {};
  const ops = [];
  Object.keys(next).forEach((path) => {
    if (prev[path] !== next[path].str) {
      ops.push({ type: "set", path, data: next[path].data });
    }
  });
  Object.keys(prev).forEach((path) => {
    if (!next[path]) ops.push({ type: "delete", path });
  });
  return ops;
}

export function chunkArray(list, size) {
  const out = [];
  for (let i = 0; i < (list || []).length; i += size) {
    out.push(list.slice(i, i + size));
  }
  return out;
}
